import React, { useEffect, useState } from 'react';
import AdminLayout from './components/adminlayout';
import { serverURL } from '../constants';
import axios from 'axios';
import { toast } from 'react-toastify';

const PREDEFINED_FEATURES = [
    'Generate 5 Sub-Topics',
    'Generate 10 Sub-Topics',
    'Generate 15 Sub-Topics',
    'Create Unlimited Courses',
    'Video & Theory Course',
    'Theory & Image Course',
    'Course In 23+ Languages',
    'AI Teacher Chat',
    'Download Certificates',
    'Lifetime Access',
    '1 Month Access',
    '1 Year Access',
    'Priority Support',
    'Custom Branding',
];

const Plans = () => {
    const [plans, setPlans] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', cost: 0, duration: '', features: '', active: true, order: 0, stripeId: '', paypalId: '', razorpayId: '', paystackId: '', flutterwaveId: '' });
    const [selectedFeatures, setSelectedFeatures] = useState([]);
    const [customFeature, setCustomFeature] = useState('');

    useEffect(() => {
        sessionStorage.setItem('darkMode', false);
        fetchPlans();
    }, []);

    async function fetchPlans() {
        try {
            const response = await axios.get(serverURL + '/api/getplans');
            setPlans(response.data);
        } catch (error) {
            console.error(error);
        }
    }

    function featuresToArray(featuresStr) {
        return (featuresStr || '').split('\n').map(f => f.trim()).filter(f => f);
    }

    function handleEdit(plan) {
        setEditing(plan._id);
        setForm({ name: plan.name, cost: plan.cost, duration: plan.duration, features: plan.features, active: plan.active, order: plan.order, stripeId: plan.stripeId || '', paypalId: plan.paypalId || '', razorpayId: plan.razorpayId || '', paystackId: plan.paystackId || '', flutterwaveId: plan.flutterwaveId || '' });
        setSelectedFeatures(featuresToArray(plan.features));
    }

    function handleNew() {
        setEditing('new');
        setForm({ name: '', cost: 0, duration: 'month', features: '', active: true, order: plans.length, stripeId: '', paypalId: '', razorpayId: '', paystackId: '', flutterwaveId: '' });
        setSelectedFeatures([]);
    }

    function toggleFeature(feature) {
        let updated;
        if (selectedFeatures.includes(feature)) {
            updated = selectedFeatures.filter(f => f !== feature);
        } else {
            updated = [...selectedFeatures, feature];
        }
        setSelectedFeatures(updated);
        setForm({ ...form, features: updated.join('\n') });
    }

    function addCustomFeature() {
        if (customFeature.trim() && !selectedFeatures.includes(customFeature.trim())) {
            const updated = [...selectedFeatures, customFeature.trim()];
            setSelectedFeatures(updated);
            setForm({ ...form, features: updated.join('\n') });
            setCustomFeature('');
        }
    }

    async function handleSave() {
        if (!form.name.trim()) {
            toast.error('Plan name is required');
            return;
        }
        try {
            const body = editing === 'new' ? { ...form } : { _id: editing, ...form };
            await axios.post(serverURL + '/api/saveplan', body);
            toast.success(editing === 'new' ? 'Plan created' : 'Plan updated');
            setEditing(null);
            fetchPlans();
        } catch (error) {
            toast.error('Error saving plan');
        }
    }

    async function handleDelete(id) {
        if (window.confirm('Delete this plan?')) {
            try {
                await axios.post(serverURL + '/api/deleteplan', { _id: id });
                toast.success('Plan deleted');
                fetchPlans();
            } catch (error) {
                toast.error('Error deleting plan');
            }
        }
    }

    return (
        <AdminLayout>
            <div className='p-6'>
                <div className='flex justify-between items-center mb-6'>
                    <div>
                        <h2 className='text-xl font-bold'>Subscription Plans</h2>
                        <p className='text-sm text-gray-500'>These plans appear on the Pricing page</p>
                    </div>
                    <button onClick={handleNew} className='bg-black text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-gray-800'>+ Add Plan</button>
                </div>

                {editing && (
                    <div className='bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden'>
                        <div className='px-6 py-4 border-b border-gray-100 bg-gray-50'>
                            <h3 className='font-bold'>{editing === 'new' ? 'New Plan' : 'Edit Plan'}</h3>
                        </div>
                        <div className='p-6'>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-5'>
                                <div>
                                    <label className='text-sm font-medium text-gray-700 block mb-1.5'>Plan Name</label>
                                    <input className='border border-gray-300 rounded-lg w-full px-3 py-2.5 text-sm' value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder='e.g. Monthly Plan' />
                                </div>
                                <div>
                                    <label className='text-sm font-medium text-gray-700 block mb-1.5'>Price ($)</label>
                                    <input className='border border-gray-300 rounded-lg w-full px-3 py-2.5 text-sm' type='number' value={form.cost} onChange={e => setForm({ ...form, cost: Number(e.target.value) })} />
                                </div>
                                <div>
                                    <label className='text-sm font-medium text-gray-700 block mb-1.5'>Duration</label>
                                    <select className='border border-gray-300 rounded-lg w-full px-3 py-2.5 text-sm' value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })}>
                                        <option value=''>Free (no duration)</option>
                                        <option value='month'>Monthly</option>
                                        <option value='year'>Yearly</option>
                                    </select>
                                </div>
                                <div>
                                    <label className='text-sm font-medium text-gray-700 block mb-1.5'>Display Order</label>
                                    <input className='border border-gray-300 rounded-lg w-full px-3 py-2.5 text-sm' type='number' value={form.order} onChange={e => setForm({ ...form, order: Number(e.target.value) })} />
                                    <p className='text-xs text-gray-400 mt-1'>0 = first on pricing page</p>
                                </div>
                            </div>

                            <div className='mb-5'>
                                <label className='text-sm font-medium text-gray-700 block mb-2'>Features</label>
                                <p className='text-xs text-gray-400 mb-3'>Select which features are included in this plan. These show on the pricing page.</p>
                                <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
                                    {PREDEFINED_FEATURES.map(feature => (
                                        <label key={feature} className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${selectedFeatures.includes(feature) ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                            <input
                                                type='checkbox'
                                                checked={selectedFeatures.includes(feature)}
                                                onChange={() => toggleFeature(feature)}
                                                className='rounded text-black focus:ring-black'
                                            />
                                            <span className='text-sm'>{feature}</span>
                                        </label>
                                    ))}
                                </div>

                                {selectedFeatures.filter(f => !PREDEFINED_FEATURES.includes(f)).length > 0 && (
                                    <div className='mt-3'>
                                        <p className='text-xs font-medium text-gray-500 mb-1'>Custom features:</p>
                                        {selectedFeatures.filter(f => !PREDEFINED_FEATURES.includes(f)).map(f => (
                                            <span key={f} className='inline-flex items-center gap-1 bg-gray-100 rounded-full px-3 py-1 text-xs mr-2 mb-1'>
                                                {f}
                                                <button onClick={() => toggleFeature(f)} className='text-gray-400 hover:text-red-500'>x</button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div className='flex gap-2 mt-3'>
                                    <input
                                        className='border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1'
                                        placeholder='Add custom feature...'
                                        value={customFeature}
                                        onChange={e => setCustomFeature(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && addCustomFeature()}
                                    />
                                    <button onClick={addCustomFeature} className='border border-gray-300 px-3 py-2 rounded-lg text-sm hover:bg-gray-50'>Add</button>
                                </div>
                            </div>

                            <div className='mb-5'>
                                <p className='text-xs text-gray-400 uppercase font-bold mb-2 mt-2'>Payment Gateway IDs</p>
                                <p className='text-xs text-gray-400 mb-3'>Enter the plan/price IDs from each payment provider. Leave empty if not using.</p>
                                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                                    <div>
                                        <label className='text-xs font-medium text-gray-600 block mb-1'>Stripe Price ID</label>
                                        <input className='border border-gray-300 rounded-lg w-full px-3 py-2 text-sm' value={form.stripeId} onChange={e => setForm({ ...form, stripeId: e.target.value })} placeholder='price_...' />
                                    </div>
                                    <div>
                                        <label className='text-xs font-medium text-gray-600 block mb-1'>PayPal Plan ID</label>
                                        <input className='border border-gray-300 rounded-lg w-full px-3 py-2 text-sm' value={form.paypalId} onChange={e => setForm({ ...form, paypalId: e.target.value })} placeholder='P-...' />
                                    </div>
                                    <div>
                                        <label className='text-xs font-medium text-gray-600 block mb-1'>Razorpay Plan ID</label>
                                        <input className='border border-gray-300 rounded-lg w-full px-3 py-2 text-sm' value={form.razorpayId} onChange={e => setForm({ ...form, razorpayId: e.target.value })} placeholder='plan_...' />
                                    </div>
                                    <div>
                                        <label className='text-xs font-medium text-gray-600 block mb-1'>Paystack Plan ID</label>
                                        <input className='border border-gray-300 rounded-lg w-full px-3 py-2 text-sm' value={form.paystackId} onChange={e => setForm({ ...form, paystackId: e.target.value })} placeholder='PLN_...' />
                                    </div>
                                    <div>
                                        <label className='text-xs font-medium text-gray-600 block mb-1'>Flutterwave Plan ID</label>
                                        <input className='border border-gray-300 rounded-lg w-full px-3 py-2 text-sm' value={form.flutterwaveId} onChange={e => setForm({ ...form, flutterwaveId: e.target.value })} placeholder='67960' />
                                    </div>
                                </div>
                            </div>

                            <div className='flex items-center gap-2 mb-5'>
                                <input type='checkbox' id='active' checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className='rounded text-black focus:ring-black' />
                                <label htmlFor='active' className='text-sm font-medium'>Active (visible on pricing page)</label>
                            </div>

                            <div className='flex gap-2'>
                                <button onClick={handleSave} className='bg-black text-white px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-800'>Save Plan</button>
                                <button onClick={() => setEditing(null)} className='border border-gray-300 px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-50'>Cancel</button>
                            </div>
                        </div>
                    </div>
                )}

                {plans.length === 0 && !editing ? (
                    <div className='bg-white rounded-lg border border-gray-200 p-12 text-center'>
                        <p className='text-gray-400 mb-3'>No plans yet</p>
                        <button onClick={handleNew} className='bg-black text-white px-4 py-2 rounded-lg font-medium text-sm'>Create your first plan</button>
                    </div>
                ) : plans.length > 0 && (
                    <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
                        <table className='w-full'>
                            <thead className='bg-gray-50 border-b border-gray-200'>
                                <tr>
                                    <th className='text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase'>Plan</th>
                                    <th className='text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase'>Price</th>
                                    <th className='text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase'>Duration</th>
                                    <th className='text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase'>Features</th>
                                    <th className='text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase'>Status</th>
                                    <th className='text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase'>Actions</th>
                                </tr>
                            </thead>
                            <tbody className='divide-y divide-gray-100'>
                                {plans.map(plan => (
                                    <tr key={plan._id} className='hover:bg-gray-50'>
                                        <td className='px-4 py-3 text-sm font-medium'>{plan.name}</td>
                                        <td className='px-4 py-3 text-sm font-bold'>${plan.cost}</td>
                                        <td className='px-4 py-3 text-sm text-gray-500'>{plan.duration ? `/${plan.duration}` : 'Free'}</td>
                                        <td className='px-4 py-3 text-sm text-gray-500'>{featuresToArray(plan.features).length} features</td>
                                        <td className='px-4 py-3'>
                                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${plan.active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                {plan.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className='px-4 py-3 flex gap-3'>
                                            <button onClick={() => handleEdit(plan)} className='text-xs font-medium text-blue-600 hover:text-blue-800'>Edit</button>
                                            <button onClick={() => handleDelete(plan._id)} className='text-xs font-medium text-red-600 hover:text-red-800'>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};
export default Plans;
